import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { useButton, useAdyen, useDispatch, useLogger } from '~Hooks';
import { BlackButton, BlueButton, Box, GreenButton, RedButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useTableApi } from '~Components/TableUniverse';
import { useStorage } from '~Components/Storage';
import { addToast, removeToast } from '~/actions';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';
import Heading from '~ComponentsImproved/Typography/Heading';

const PaymentRow = styled(Box)`
  background-color: var(--colorBlueGray1000);
  margin-bottom: 8px;
  border-radius: 8px;
  padding: 12px;
`;

const PaymentInfo = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
`;

const TerminalSelectionWrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  min-width: 400px;
`;

const TerminalButton = styled(GreenButton)`
  width: 100%;
  margin-bottom: 8px;
`;

interface PaymentRowItemProps {
  payment: any;
  loading: boolean;
  staffCan: any;
  i18n: any;
  currency: any;
  onVoidClick: (paymentId: string) => void;
  chequeId: string;
}

const OngoingChequePayment: FC<PaymentRowItemProps> = ({
  payment,
  loading,
  staffCan,
  i18n,
  currency,
  onVoidClick,
  chequeId,
}) => {
  const logger = useLogger('history');
  const dispatch = useDispatch();
  const { refundPayment: adyenRefund } = useAdyen();
  const { refundPayment: tableRefund } = useTableApi();
  const [storage] = useStorage();
  const { adyenTerminals } = storage;

  // State for refund functionality
  const [refunding, setRefunding] = useState(false);
  const terminalSelectionModal = useModal();

  // Handle refund process with selected terminal
  const handleRefund = (terminalId: string) => {
    logger.info('Starting payment refund on active cheque', {
      paymentId: payment.id,
      chequeId,
      transactionId: payment.transactionId,
      terminalId,
      amount: payment.amount,
    });
    setRefunding(true);

    // Show progress toast
    dispatch(addToast(i18n('TOAST.Refund.Progress'), false, payment.id));

    // Call Adyen refund with payment details
    adyenRefund({
      transactionId: payment.transactionId!,
      createdAt: payment.createdAt,
      terminalId,
    }).then(({ success, message }) => {
      if (success) {
        logger.info('Payment refund successful on active cheque', {
          paymentId: payment.id,
          chequeId,
          transactionId: payment.transactionId,
        });
        // Remove progress toast and show success
        dispatch(removeToast(payment.id));
        dispatch(addToast(i18n('TOAST.Refund.Successful'), true));

        // Mark payment as refunded in table universe
        tableRefund(payment.id, chequeId);
      } else {
        logger.error('Payment refund failed on active cheque', {
          paymentId: payment.id,
          chequeId,
          transactionId: payment.transactionId,
          errorMessage: message,
        });
        // Remove progress toast and show failure
        dispatch(removeToast(payment.id));
        dispatch(addToast(i18n('TOAST.Refund.Failed'), true));
      }
      setRefunding(false);
      terminalSelectionModal.toggle();
    });
  };

  // Button for void action
  const voidButton = useButton({
    onClick: () => {
      onVoidClick(payment.id);
    },
    disabled: loading || !staffCan.VOID_PAYMENT,
  });

  // Button for refund action - opens terminal selection modal
  const refundButton = useButton({
    onClick: () => {
      terminalSelectionModal.toggle();
    },
    disabled: refunding || loading || payment.refunded || !staffCan.REFUND,
  });

  return (
    <>
      <PaymentRow>
        <PaymentInfo>
          <Paragraph level={3} color="var(--colorWhite)" bottom={4}>
            {DateTime.fromISO(payment.createdAt).toFormat('yyyy-MM-dd HH:mm')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)" bottom={4}>
            {currency.format(payment.amount)}
          </Paragraph>
        </PaymentInfo>
        <Box marginTop="xs">
          <Paragraph level={5} color="var(--colorBlueGray200)" bottom={8}>
            {i18n('History.TransactionId')}: {payment.transactionId || 'N/A'}
          </Paragraph>
          <Paragraph level={5} color="var(--colorBlueGray200)" bottom={8}>
            {i18n('History.PaymentId')}: {payment.id}
          </Paragraph>
          {payment.refunded && (
            <Paragraph level={5} color="var(--colorUIRed)" bottom={8}>
              {i18n('History.Refunded')}
            </Paragraph>
          )}
        </Box>
        <Box display="flex" gap="xs" marginTop="small">
          {payment.type == 'ADYEN' && (
            <RedButton {...refundButton}>
              {refunding ? i18n('History.Refunding') : i18n('History.Refund')}
            </RedButton>
          )}
          <BlackButton {...voidButton}>{i18n('History.Void')}</BlackButton>

          {/* Todo implement function to move payment to another cheque */}
          {/* <BlueButton disabled>{i18n('History.Move')}</BlueButton> */}
        </Box>
      </PaymentRow>

      {/* Terminal selection modal */}
      <Modal isShowing={terminalSelectionModal.isShowing} hide={terminalSelectionModal.toggle}>
        <TerminalSelectionWrapper padding="large">
          <Heading level={2} color="var(--colorWhite)" bottom={16}>
            {i18n('History.ChooseRefundTerminal')}
          </Heading>
          <Box>
            {adyenTerminals.map((terminal) => (
              <TerminalButton
                key={terminal.POIID}
                onClick={() => handleRefund(terminal.POIID)}
                disabled={refunding}>
                {terminal.POIID}
              </TerminalButton>
            ))}
          </Box>
        </TerminalSelectionWrapper>
      </Modal>
    </>
  );
};

export default OngoingChequePayment;
